import React, { useState } from 'react';
import { Outlet, NavLink, useLocation, Navigate } from 'react-router';
import { useStore } from './store/useStore';
import { useAuthStore } from './store/useAuthStore';
import { Avatar } from './components/UI';
import { MessageSquare, Search, Users, BarChart2, Settings, LogOut, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

const navItems = [
  { path: '/',        icon: MessageSquare, label: 'Тикеты',     id: 'inbox' },
  { path: '/search',  icon: Search,        label: 'Поиск',      id: 'search' },
  { path: '/users',   icon: Users,         label: 'Клиенты',    id: 'users' },
  { path: '/stats',   icon: BarChart2,     label: 'Статистика', id: 'stats' },
  { path: '/settings',icon: Settings,      label: 'Настройки',  id: 'settings' },
] as const;

export const Layout = () => {
  const { currentOperatorId, operators, tickets, loadError, setLoadError } = useStore();
  const { isAuthenticated, isBootstrapping, operator: authOperator, logout } = useAuthStore();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // ── Auth guard ────────────────────────────────────────────────────────
  if (isBootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F7] text-[#8E8E93]">
        Проверяем operator session...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const unreadCount = tickets.reduce((sum, t) => sum + (t.isRead ? 0 : t.unreadCount), 0);
  const me = operators.find(o => o.id === currentOperatorId);
  const displayName = authOperator?.name || me?.name || 'Оператор';

  return (
    <div className="flex h-[100dvh] w-full bg-[#ECECF0] overflow-hidden text-black font-[SF_Pro_Text,system-ui,sans-serif]">

      {/* ── Desktop Sidebar ─────────────────────────────────────────────── */}
      <div className="hidden md:flex flex-col w-[68px] bg-[#E3E3E8] border-r border-black/[0.08] items-center py-4 justify-between h-full flex-shrink-0 z-20">
        <div className="flex flex-col gap-1 w-full items-center px-2">
          {navItems.map((item) => {
            const isActive = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                title={item.label}
                className={`w-11 h-11 rounded-[12px] flex items-center justify-center relative transition-all duration-150 ${
                  isActive
                    ? 'bg-[#007AFF] text-white shadow-[0_2px_10px_rgba(0,122,255,0.35)]'
                    : 'text-black/45 hover:bg-black/6 hover:text-black/70'
                }`}
              >
                <item.icon size={21} strokeWidth={isActive ? 2 : 1.8} />
                {item.id === 'inbox' && unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#FF3B30] rounded-full ring-1.5 ring-[#E3E3E8]" />
                )}
              </NavLink>
            );
          })}
        </div>

        {/* Operator avatar + logout */}
        <div className="flex flex-col items-center gap-2 px-2 w-full relative">
          <AnimatePresence>
            {showUserMenu && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute bottom-full mb-2 left-[120px] w-52 bg-white rounded-[16px] shadow-[0_8px_32px_rgba(0,0,0,0.16)] border border-black/8 overflow-hidden py-1"
              >
                <div className="px-4 py-2.5 border-b border-black/5">
                  <p className="text-[13px] font-semibold text-black truncate">{displayName}</p>
                  <p className="text-[11px] text-[#8E8E93] mt-0.5">
                    {authOperator?.role === 'OWNER'
                      ? 'Owner'
                      : authOperator?.role === 'ADMIN'
                        ? 'Администратор'
                        : authOperator?.role === 'VIEWER'
                          ? 'Наблюдатель'
                          : 'Оператор'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    void logout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#FF3B30] hover:bg-[#FF3B30]/6 transition-colors"
                >
                  <LogOut size={16} strokeWidth={2} />
                  Выйти
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <button
            onClick={() => setShowUserMenu(v => !v)}
            className="relative hover:opacity-80 transition-opacity rounded-full ring-2 ring-transparent hover:ring-black/10 cursor-pointer"
            title={displayName}
          >
            {me && <Avatar name={displayName} size="36px" isOnline={me.isOnline} />}
          </button>
        </div>
      </div>

      {/* ── Main Content ────────────────────────────────────────────────── */}
      <div className="flex-1 flex w-full h-full overflow-hidden relative bg-white rounded-tl-[12px] md:shadow-[-2px_0_0_rgba(0,0,0,0.06)]">
        {loadError && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 max-w-[90%] md:max-w-[640px] bg-[#FFECEC] border border-[#FFB7B7] text-[#9F1C1C] px-4 py-3 rounded-[14px] shadow-sm flex items-start gap-3">
            <div className="text-[13px] font-medium leading-5 break-words">{loadError}</div>
            <button
              onClick={() => setLoadError(null)}
              className="shrink-0 text-[#9F1C1C]/70 hover:text-[#9F1C1C]"
              aria-label="Закрыть"
            >
              ×
            </button>
          </div>
        )}
        <Outlet />
      </div>

      {/* ── Mobile Tab Bar ──────────────────────────────────────────────── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50">
        <div className="h-[72px] pb-[env(safe-area-inset-bottom)] bg-white/92 backdrop-blur-2xl border-t border-black/[0.08] flex items-center justify-around px-1">
          {navItems.map((item) => {
            const isActive = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className="flex flex-col items-center justify-center w-[60px] h-full pt-1.5 relative"
              >
                <div className={`relative p-1.5 rounded-[10px] transition-all ${isActive ? 'text-[#007AFF]' : 'text-[#8E8E93]'}`}>
                  <item.icon size={23} strokeWidth={isActive ? 2 : 1.6} />
                  {item.id === 'inbox' && unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-[#FF3B30] rounded-full ring-2 ring-white" />
                  )}
                </div>
                <span className={`text-[10px] mt-0.5 font-medium ${isActive ? 'text-[#007AFF]' : 'text-[#8E8E93]'}`}>
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
};
